import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, FormRef } from "@jobber/components/Form";
import { InputPhoneNumber } from "@jobber/components/InputPhoneNumber";
import AuthFlowFrame from "components/AuthFlowFrame";
import ProXtraPromotionalPanel from "components/ProXtraPromotionalPanel";
import ProXtraSignUpFooter from "components/ProXtraSignUpFooter";
import { useProXtraFlow } from "context";
import { stripPhoneFormatting } from "helpers";
import { sendVerificationCode } from "services";

function Step1() {
    const navigate = useNavigate();
    const { setPhoneNumber } = useProXtraFlow();

    const formRef = useRef<FormRef>(null);
    const [phoneValue, setPhoneValue] = useState("");
    const [serverError, setServerError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handlePhoneChange = (value: string) => {
        setPhoneValue(value);
        if (serverError) setServerError(null);
    };

    // Only fires if the Form's validation passed.
    const handleSubmit = async () => {
        setServerError(null);
        setIsSubmitting(true);

        try {
            await sendVerificationCode(stripPhoneFormatting(phoneValue));
            setPhoneNumber(phoneValue);
            navigate("/jobber-homedepot-step2");
        } catch {
            setServerError(
                "We couldn't send the code. Check the phone number and try again.",
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AuthFlowFrame
            pretitle="Connect your Pro Xtra account"
            title="Enter your phone number"
            description="Enter the phone number linked to your Pro Xtra account. We'll send you a verification code."
            serverError={serverError}
            sidePanel={<ProXtraPromotionalPanel />}
            secondaryButton={{
                text: "Back",
                url: "https://secure.getjobber.com/marketplace",
            }}
            primaryButton={{
                text: "Send Verification Code",
                onClick: () => formRef.current?.submit(),
                disabled: isSubmitting,
                loading: isSubmitting,
            }}
            footer={<ProXtraSignUpFooter />}
        >
            <div style={{ maxWidth: "440px" }}>
                <Form ref={formRef} onSubmit={handleSubmit}>
                    <InputPhoneNumber
                        value={phoneValue}
                        onChange={handlePhoneChange}
                        placeholder="Phone number"
                        validations={{
                            required: {
                                value: true,
                                message: "Phone number is required",
                            },
                            validate: (value: string) => {
                                const digits = stripPhoneFormatting(value);
                                return (
                                    digits.length === 10 ||
                                    "Enter a valid phone number"
                                );
                            },
                        }}
                    />
                </Form>
            </div>
        </AuthFlowFrame>
    );
}

export default Step1;
